<script setup lang="ts">
import { computed, ref, watch } from 'vue'

const props = defineProps<{
  events: Array<{ node: string; status: string; detail: string; timestamp: string }>
  currentNode: string
  /** SSE 是否已收到 replay_done (历史重放完毕, 事件均为实时) */
  live?: boolean
  /** 本次运行深度 (ALENS-193): 决定展示的阶段集合, 与服务端
   *  alphalens/graph/execution_plan.py 的节点/路由单一来源保持同构。 */
  depth?: 'quick' | 'standard' | 'deep'
  /** state.skipped_nodes (ALENS-192): 节点 -> 跳过原因 (depth_quick / already_completed) */
  skippedNodes?: Record<string, string>
}>()

const NODE_LABELS: Record<string, string> = {
  init: '初始化',
  intent: '意图解析',
  macro: '宏观+期货',
  fundamental: '基本面',
  technical: '技术面',
  sentiment: '舆情情绪',
  deep_research: '深度研究',
  bull_r1: '多头立论 R1',
  bear_r1: '空头反驳 R1',
  bull_r2: '多头立论 R2',
  bear_r2: '空头反驳 R2',
  research_manager: '研究主管裁决',
  trader: '交易提案',
  risk_committee: '风控质询',
  decision: '最终决策',
  report: '生成报告',
  done: '完成',
  // 兼容旧事件名 (ALENS-192 前的单轮命名)
  bull: '多头立论',
  bear: '空头反驳',
  debate: '多空辩论',
}

/** 与 execution_plan.build_execution_plan 同构的阶段序列:
 *  quick 短路 (intent→technical→交易链), standard 单轮辩论, deep 两轮。 */
function stagesForDepth(depth?: string): string[] {
  const head = ['init', 'intent']
  const analysts = ['macro', 'fundamental', 'technical', 'sentiment']
  const tail = ['trader', 'risk_committee', 'decision', 'report', 'done']
  if (depth === 'quick') return [...head, 'technical', ...tail]
  const debate =
    depth === 'deep'
      ? ['deep_research', 'bull_r1', 'bear_r1', 'bull_r2', 'bear_r2', 'research_manager']
      : ['deep_research', 'bull_r1', 'bear_r1', 'research_manager']
  return [...head, ...analysts, ...debate, ...tail]
}

const ORDERED_NODES = computed(() => stagesForDepth(props.depth))

/** 旧事件名归一到当前阶段键 (bull→bull_r1; 单轮年代无 r2 事件)。 */
function normalizeNode(node: string): string {
  if (node === 'bull' || node === 'bear') return `${node}_r1`
  return node
}

type StepStatus = 'pending' | 'running' | 'completed' | 'failed' | 'skipped'

/** 单条进度事件 -> 阶段状态 (done 伪节点吃 started/completed 两种完成信号)。 */
function applyEventStatus(map: Record<string, StepStatus>, node: string, status: string): void {
  if (node === 'done') {
    map[node] = status === 'failed' ? 'failed' : 'completed'
  } else if (status === 'started') {
    map[node] = 'running'
  } else if (status === 'completed') {
    map[node] = 'completed'
  } else if (status === 'failed') {
    map[node] = 'failed'
  }
}

/** 跳过记录 (ALENS-192 skipped_nodes): 未执行的节点标注原因; 已有完成态的事件优先。 */
function applySkippedStatus(map: Record<string, StepStatus>, name: string, reason: string): void {
  const n = normalizeNode(name)
  if (n in map && map[n] === 'pending') {
    map[n] = reason === 'already_completed' ? 'completed' : 'skipped'
  }
}

const nodeStatus = computed<Record<string, StepStatus>>(() => {
  const map: Record<string, StepStatus> = {}
  for (const n of ORDERED_NODES.value) map[n] = 'pending'
  for (const e of props.events) {
    const n = normalizeNode(e.node)
    if (n in map) applyEventStatus(map, n, e.status)
  }
  for (const [name, reason] of Object.entries(props.skippedNodes || {})) {
    applySkippedStatus(map, name, reason)
  }
  return map
})

const SKIP_REASONS: Record<string, string> = {
  depth_quick: '深度短路',
  already_completed: '断点续跑复用',
}

function statusColor(s: StepStatus | undefined) {
  return (
    {
      pending: 'var(--border-color)',
      running: 'var(--accent)',
      completed: 'var(--success)',
      failed: 'var(--danger)',
      skipped: 'var(--border-color)',
    }[s || 'pending'] || 'var(--border-color)'
  )
}
function statusText(s: StepStatus | undefined, node?: string) {
  if (s === 'skipped') {
    const reason = props.skippedNodes?.[node || ''] || ''
    return `跳过·${SKIP_REASONS[reason] || reason}`
  }
  return (
    { pending: '等待', running: '进行中', completed: '完成', failed: '失败' }[s || 'pending'] ||
    s ||
    ''
  )
}

const isFinished = computed(() => {
  const status = nodeStatus.value.done
  return status === 'completed' || status === 'failed'
})

const activeNames = ref<string[]>(['logs'])

watch(isFinished, (finished) => {
  if (finished) {
    activeNames.value = []
  } else {
    activeNames.value = ['logs']
  }
}, { immediate: true })
</script>

<template>
  <el-card class="progress-card" shadow="hover">
    <template #header>
      <span class="title">分析进度</span>
      <el-tag v-if="depth === 'quick'" size="small" type="info" style="margin-left: 8px">
        quick 短路（技术面直进交易链）
      </el-tag>
      <span v-if="live" class="live-badge" title="历史事件已重放完毕, 正在接收实时进度">● 实时</span>
    </template>
    <div class="pipeline">
      <div
        v-for="node in ORDERED_NODES"
        :key="node"
        class="step"
        :class="{ skipped: nodeStatus[node] === 'skipped' }"
        :style="{ borderColor: statusColor(nodeStatus[node]) }"
      >
        <div class="step-label">{{ NODE_LABELS[node] || node }}</div>
        <div
          class="step-status"
          :style="{ color: statusColor(nodeStatus[node]) }"
          :title="skippedNodes?.[node] || ''"
        >
          {{ statusText(nodeStatus[node], node) }}
        </div>
      </div>
    </div>
    <el-divider />
    <el-collapse v-model="activeNames" style="border: none;">
      <el-collapse-item name="logs" title="查看详细执行日志">
        <el-timeline style="margin-top: 10px;">
          <el-timeline-item
            v-for="(e, i) in events"
            :key="i"
            :timestamp="e.timestamp"
            :type="e.status === 'failed' ? 'danger' : e.status === 'completed' ? 'success' : 'primary'"
          >
            <strong>{{ NODE_LABELS[normalizeNode(e.node)] || e.node }}</strong>: {{ e.detail }}
          </el-timeline-item>
        </el-timeline>
      </el-collapse-item>
    </el-collapse>
  </el-card>
</template>

<style scoped>
.title { font-size: 18px; font-weight: 600; color: var(--text-primary); }
.live-badge { margin-left: 10px; font-size: 12px; color: #22c55e; animation: live-pulse 2s ease-in-out infinite; }
@keyframes live-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
.pipeline {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 8px 0;
}
.step {
  flex: 1;
  min-width: 90px;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  padding: 6px 8px;
  text-align: center;
}
.step.skipped {
  border-style: dashed;
  opacity: 0.65;
}
.step-label { font-size: 13px; color: var(--text-primary); }
.step-status { font-size: 12px; margin-top: 2px; }
</style>
